'use client';

import { useEffect, useRef } from 'react';

import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type CategoryChip = { key: string; label: string; href: string; count?: number };

/**
 * Category filter of the blog (Figma „Filter chips“ 60391:78422): pill links, the current one in
 * bg/inverse. Desktop: 48 px, wrapping; phones (60460:110231): 40 px with the article count, one row
 * that scrolls sideways under the page gutter and starts scrolled to the current chip.
 */
export function CategoryChips({ chips, current }: { chips: CategoryChip[]; current: string }) {
  const { t } = useTranslations();
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const row = list.current;
    const active = row?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!row || !active || row.scrollWidth <= row.clientWidth) return;
    row.scrollLeft = active.offsetLeft - parseFloat(getComputedStyle(row).paddingLeft);
  }, [current]);

  return (
    <nav aria-label={t('text176')}>
      <ul
        ref={list}
        className="hide-scroll-bar -mx-4 flex gap-2 overflow-x-auto px-4 md:-mx-6 md:px-6 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0"
      >
        {chips.map(chip => {
          const selected = chip.key === current;
          return (
            <li key={chip.key} className="flex shrink-0">
              <Link
                href={chip.href}
                aria-current={selected ? 'page' : undefined}
                className={cn(
                  'dlz-body-s-medium inline-flex h-10 items-center gap-2 rounded-full border px-4 transition-colors lg:h-12',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2',
                  selected
                    ? 'border-[#0F1012] bg-[#0F1012] text-white'
                    : 'border-[#E2E4E7] bg-white text-[#0F1012] hover:border-[#9096A0]',
                )}
              >
                <span className="lg:dlz-trim">{chip.label}</span>
                {chip.count !== undefined ? (
                  <span
                    className={cn(
                      'dlz-caption lg:hidden',
                      selected ? 'text-[#B9BDC3]' : 'text-[#6B7079]',
                    )}
                  >
                    {chip.count}
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
